import React, { useMemo } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import GuidedPreparationQuestion from './GuidedPreparationQuestion';
import { mergeJourneyAnswers } from './journeyAnswerModel';
import useJourneyAnswers from './useJourneyAnswers';

const GuidedPreparationQuestions = ({
  childId, draftIdentity, questions = [], proposedAnswers = [], savedAnswers = [],
  evidence = [], answerFreshness = [], canEdit, accent, onSaved, onViewSources, presentation = 'default',
}) => {
  const answerState = useJourneyAnswers({ childId, draftIdentity, savedAnswers, onSaved });
  const items = useMemo(() => mergeJourneyAnswers({
    questions, proposedAnswers, savedAnswers: answerState.answers, answerFreshness,
  }), [answerFreshness, answerState.answers, proposedAnswers, questions]);

  const isCustomCards = presentation === 'custom_cards';
  const questionCards = items.map((item, index) => (
    <GuidedPreparationQuestion
      key={item.question.id}
      item={item}
      evidence={evidence}
      index={index}
      canEdit={canEdit}
      accent={accent}
      saving={Boolean(answerState.savingQuestionId)}
      onConfirmSuggested={answerState.confirmSuggested}
      onSaveOwn={answerState.saveCaregiverAnswer}
      onSkip={answerState.skipAnswer}
      onRestore={answerState.restoreAnswer}
      onViewSources={onViewSources}
      presentation={presentation}
    />
  ));

  if (isCustomCards) {
    return (
      <Box>
        {answerState.error ? <Alert severity="error" sx={{ mb: 1.2 }}>{answerState.error}</Alert> : null}
        <Box sx={{ mb: 1.1, px: { xs: 0.4, md: 0.6 } }}>
          <Typography component="h2" sx={{ fontSize: { xs: '1.15rem', md: '1.32rem' }, fontWeight: 800, color: colors.semantic.ink }}>
            Prepare for your conversation
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: { xs: '0.84rem', md: '0.9rem' }, lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.65) }}>
            Review what Lifelog found, add your context, or leave out anything you do not need.
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: { xs: 1.15, md: 1.5 } }}>{questionCards}</Box>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', border: `1px solid ${alpha(accent.primary, 0.2)}` }}>
      <Box sx={{ px: { xs: 1.4, md: 1.8 }, py: 1.4, bgcolor: alpha(accent.primary, 0.06) }}>
        <Typography component="h2" sx={{ fontSize: { xs: '1.15rem', md: '1.3rem' }, fontWeight: 800, color: colors.semantic.ink }}>
          Prepare for your conversation
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: '0.84rem', lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.65) }}>
          Review what Lifelog found, add your context, or leave out anything you do not need.
        </Typography>
      </Box>
      {answerState.error ? <Alert severity="error" sx={{ m: 1 }}>{answerState.error}</Alert> : null}
      {questionCards}
    </Paper>
  );
};

export default GuidedPreparationQuestions;
